import { Children, forwardRef, type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { Tabs as MuiTabs, type TabsProps as MuiTabsProps } from "@mui/material";
import { Tab } from "./Tab";
import { produce } from "immer";

export type TabsProps = MuiTabsProps & {
  /**
   * If true, the indicator will span the full width of the tab.
   * If false, the indicator will only span the width of the label.
   * @default false
   */
  fullWidthIndicator?: boolean;
};

export const Tabs = forwardRef<HTMLDivElement, TabsProps>((props, ref) => {
  const { children, value, fullWidthIndicator = true, ...rest } = props;
  const [labelWidths, setLabelWidths] = useState<{ [key: number]: number }>({});
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});
  const tabsRef: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

  const handleLabelWidthChange = useCallback((index: number, width: number) => {
    setLabelWidths((prev) =>
      produce(prev, (draft) => {
        draft[index] = width;
      }),
    );
  }, []);

  useEffect(() => {
    if (tabsRef.current && value !== undefined && value !== null) {
      const tabs = tabsRef.current.querySelectorAll('[role="tab"]');
      let activeTabIndex: number;

      if (typeof value === "number") {
        activeTabIndex = value;
      } else {
        // Find the index by matching child props value
        activeTabIndex = Children.toArray(children).findIndex(
          (child) => child && typeof child === "object" && "props" in child && child.props.value === value,
        );
      }

      const activeTab = tabs[activeTabIndex] as HTMLElement;
      if (activeTab) {
        if (fullWidthIndicator) {
          setIndicatorStyle({}); // Use default MUI behavior
        } else if (labelWidths[activeTabIndex]) {
          const tabWidth = activeTab.offsetWidth;
          const labelWidth = labelWidths[activeTabIndex];
          const left = activeTab.offsetLeft + (tabWidth - labelWidth) / 2;

          setIndicatorStyle({
            left,
            width: labelWidth,
          });
        }
      }
    }
  }, [value, labelWidths, fullWidthIndicator]);

  return (
    <MuiTabs
      ref={(node) => {
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        tabsRef.current = node;
      }}
      value={value}
      TabIndicatorProps={{
        style: fullWidthIndicator ? undefined : indicatorStyle,
      }}
      {...rest}
    >
      {Children.map(children, (child, index) => {
        if (!child) {
          return child;
        }

        return typeof child === "object" && "type" in child && child.type === Tab
          ? {
              ...child,
              props: {
                ...child.props,
                onLabelWidthChange: fullWidthIndicator
                  ? undefined
                  : (width: number) => handleLabelWidthChange(index, width),
              },
            }
          : child;
      })}
    </MuiTabs>
  );
});

Tabs.displayName = "Tabs";
